import * as React from 'react';
import { addImageParams, AddImageParamTypes, getCategoryPageUrl } from '@/utils/urls';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { Category } from '../../types/CategoryTypes';
import { CategoryNamesAndIds } from '../../constants/constant';
import { Collapse } from 'react-collapse';
import { FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { urlToSend } from '../utils/urlToSend';
import FormattedMessage from '@/components/Formatted/FormattedMessageText';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled from 'styled-components';

export type DropDownProps = {
    categories: Category[];
    index: number;
    title: string;
};

const DropDown = ({ categories, index, title }: DropDownProps) => {
    const [show, setShow] = React.useState(false);
    const { formatMessage } = useIntl();
    return (
        <div>
            {index === 0 && <LineSeperator />}
            <HeaderWrapper onClick={() => setShow(!show)}>
                <HeaderTitle>
                    <FormattedMessage
                        id="discoverPage.subCategory.subCategoryTitle"
                        values={{ title }}
                    />
                </HeaderTitle>
                <CaretArrowContainer>
                    {show ? (
                        <CaretUp
                            size="xs"
                            style="fill"
                        />
                    ) : (
                        <CaretDown
                            size="xs"
                            style="fill"
                        />
                    )}
                </CaretArrowContainer>
            </HeaderWrapper>
            <Collapse isOpened={show}>
                <ContentWrapper>
                    {categories.map((category) => {
                        const imageParams: AddImageParamTypes = {
                            height: 157,
                            url: category.imageUrl,
                        };

                        return (
                            <StyledSubCategoryLink
                                key={category.title}
                                route
                                to={category.pageUrl}
                            >
                                <StyledImageContainer>
                                    <StyledImage
                                        alt={category.title}
                                        loading="lazy"
                                        src={addImageParams(imageParams)}
                                    />
                                </StyledImageContainer>
                                <StyledCategoryContent>
                                    <BodyPrimary>
                                        <FormattedMessage
                                            id="discoverPage.subCategory.subCategoryTitleSpan"
                                            values={{ title: category.title }}
                                        />
                                    </BodyPrimary>
                                    {category.followers > 0 && (
                                        <BodyPrimary color="secondary">
                                            {category.followers === 1 ? (
                                                <FormattedMessage id="discoverPage.subCategory.subCategoryFollower" />
                                            ) : (
                                                <FormattedMessage
                                                    id="discoverPage.subCategory.subCategoryFollowers"
                                                    values={{
                                                        followers: (
                                                            <FormattedNumber
                                                                key="followers"
                                                                value={category.followers}
                                                            />
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </BodyPrimary>
                                    )}
                                </StyledCategoryContent>
                            </StyledSubCategoryLink>
                        );
                    })}
                </ContentWrapper>
                <SeeAllCategoriesLinkContainer>
                    <SeeAllCategoriesLink
                        size="sm"
                        suppressHydrationWarning
                        to={getCategoryPageUrl(urlToSend(title.toLocaleLowerCase()), `${CategoryNamesAndIds[title]}`)}
                    >
                        {formatMessage({ id: 'discoverPage.subCategory.subCategoryLink' }, { title })}
                    </SeeAllCategoriesLink>
                </SeeAllCategoriesLinkContainer>
            </Collapse>
            <LineSeperator />
        </div>
    );
};

export default DropDown;

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const HeaderTitle = styled(H2)`
    margin: 24px 0;
`;

const CaretArrowContainer = styled.div`
    display: flex;
    align-items: center;
`;

const LineSeperator = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;

    @media (max-width: 540px) {
        justify-content: space-around;
    }
`;

const StyledSubCategoryLink = styled(Link)`
    text-decoration: none;
    margin-right: 10px;
    width: 157px;
`;

const StyledImageContainer = styled.div`
    align-items: center;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    position: relative;
    width: 157px;
    height: 157px;
`;

const StyledImage = styled.img`
    border-radius: 7px;
    padding: 5px 0;
    object-fit: cover;
    max-height: 157px;
    max-width: 157px;
`;

const StyledCategoryContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px 0 8px 0;

    &:active {
        span {
            color: ${({ theme }) => theme.colors.blue200};
            text-decoration: underline;
        }
    }
`;

const SeeAllCategoriesLinkContainer = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 32px 0;
`;
const SeeAllCategoriesLink = styled(RouteButton)`
    width: fit-content;
`;
